import EventEmitter from 'eventemitter3'
import * as THREE from 'three'
import { DEG2RAD } from 'three/src/math/MathUtils.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'

import { EditorAssetOrbit } from './EditorAssetOrbit'
import { EditorAssetGrid } from './EditorAssetGrid'
import { EditorAssetGizmo } from './EditorAssetGizmo'
import { EditorAssetLayers } from './EditorAssetLayers'

import { buildNodeFaces } from '../core/buildNode'
import { bindTextureToAsset } from '../core/bindTextureToAsset'
import { cloneDeep } from 'lodash-es'
import { MAX_LIGHT_LEVEL } from '../core/chunks'
import { AtlasMaterial } from '../core/AtlasMaterial'
import { AtlasRenderer } from '../core/AtlasRenderer'
import { Atlas } from '../core/Atlas'
import { createSky } from '../core/createSky'
import { degreesToEuler } from '../core/eulerToDegrees'
import { downloadFile } from '../core/downloadFile'

const v1 = new THREE.Vector3()
const m1 = new THREE.Matrix4()

export class EditorAssetEngine extends EventEmitter {
  constructor() {
    super()
    window.assetEngine = this
  }

  init({ viewport }) {
    this.textureLoader = new THREE.TextureLoader()

    this.viewport = viewport
    this.width = this.viewport.offsetWidth
    this.height = this.viewport.offsetHeight
    this.aspect = this.width / this.height
    this.scene = new THREE.Scene()

    this.renderer = new THREE.WebGLRenderer({
      powerPreference: 'high-performance',
      antialias: true,
    })
    this.renderer.setSize(this.width, this.height)
    this.renderer.setClearColor(0xffffff, 0)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.toneMapping = THREE.NoToneMapping // THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy()
    THREE.Texture.DEFAULT_ANISOTROPY = this.maxAnisotropy
    this.resizer = new ResizeObserver(() => {
      this.resize(this.viewport.offsetWidth, this.viewport.offsetHeight)
    })
    this.viewport.appendChild(this.renderer.domElement)
    this.resizer.observe(this.viewport)

    this.cameraOrth = new THREE.OrthographicCamera()
    this.cameraOrth.position.z = 150
    this.cameraOrth.lookAt(0, 0, 0)
    this.resizeOrtho()

    this.cameraPers = new THREE.PerspectiveCamera(70, 0, 0.01, 600)
    this.cameraPers.position.z = 150
    this.camera = this.cameraPers // default

    this.active = true
    this.renderer.setAnimationLoop(this.update)

    this.rig = new THREE.Object3D()
    this.rig.add(this.cameraPers)
    // this.rig.add(this.cameraOrth)
    this.rig.rotation.reorder('YXZ')
    this.rig.rotation.x += -25 * DEG2RAD
    this.rig.rotation.y += -45 * DEG2RAD

    this.scene.add(this.rig)

    this.grid = new EditorAssetGrid(this)
    this.scene.add(this.grid.mesh)

    this.orbit = new EditorAssetOrbit(this)
    this.gizmo = new EditorAssetGizmo(this)

    this.sky = createSky()
    this.sky.time = 0.5
    this.sky.load('/skyLUT.png')
    // this.scene.add(this.sky.mesh)

    this.material = new AtlasMaterial({
      anisotropy: this.maxAnisotropy,
      skyUniforms: this.sky.uniforms,
    })
    this.atlasRenderer = new AtlasRenderer(this.material, () => this.canvas)
    this.atlas = new Atlas({ label: 'StudioAssetEngine', renderer: this.atlasRenderer })

    this.iconBG = new THREE.Mesh(
      new THREE.PlaneGeometry(32 * 1.8, 32 * 1.8), // 57.6m = 180px on screen
      new THREE.MeshBasicMaterial()
    )
    this.iconBG.material.map = this.textureLoader.load('/icon-bg.png')
    this.iconBG.material.map.colorSpace = THREE.SRGBColorSpace
    this.iconBG.material.needsUpdate = true
    this.iconBG.position.z = -100
    this.scene.add(this.iconBG)

    this.objByNodeId = {}
  }

  resize(width, height) {
    this.width = width
    this.height = height
    this.aspect = this.width / this.height
    this.renderer.setSize(this.width, this.height)
    this.cameraPers.aspect = this.aspect
    this.cameraPers.updateProjectionMatrix()
    this.resizeOrtho()
    this.emit('resize')
    this.render()
  }

  resizeOrtho() {
    const css = v1
    this.renderer.getSize(css)
    const pr = 1 // this.renderer.getPixelRatio()
    const width = Math.round(css.x * pr)
    const height = Math.round(css.y * pr)

    const pixelsPerMeter = 100 / 32 // 32m (block) -> 100px css size on screen!
    const worldWidth = width / pixelsPerMeter
    const worldHeight = height / pixelsPerMeter

    this.cameraOrth.left = -worldWidth / 2
    this.cameraOrth.right = worldWidth / 2
    this.cameraOrth.top = worldHeight / 2
    this.cameraOrth.bottom = -worldHeight / 2
    this.cameraOrth.zoom = 1
    this.cameraOrth.updateProjectionMatrix()
  }

  update = delta => {
    this.render()
  }

  render() {
    this.renderer.render(this.scene, this.camera)
  }

  build() {
    const asset = this.asset
    const texture = this.texture
    const ui = this.ui
    this.canvas = texture.canvas
    if (this.root) {
      this.scene.remove(this.root)
      this.root.traverse(node => {
        if (node.geometry) node.geometry.dispose()
      })
      this.root = null
    }
    this.objByNodeId = {}
    this.root = new THREE.Object3D()
    this.base = new THREE.Object3D()
    this.root.add(this.base)
    // this.base.scale.setScalar(1 / 32)
    this.atlas.clear()
    bindTextureToAsset(this.texture, this.asset, this.atlas)
    this.buildChildren(this.base, this.asset.model.root)
    this.scene.add(this.root)
    if (this.mode === 'build') {
      // block center bottom
      this.root.position.set(0, 0, 0)
      this.root.rotation.set(0, 0, 0)
      this.root.scale.set(1, 1, 1)
      this.base.position.set(0, 0, 0)
      this.base.rotation.set(0, 0, 0)
      this.base.scale.set(1, 1, 1)
      this.gizmo.select(this.objByNodeId[this.nodeId], true)
    }
    if (this.mode === 'pose') {
      // block center
      const pose = asset.pose[ui.pose]
      this.root.position.fromArray(pose.position)
      degreesToEuler(pose.rotation, this.root.rotation)
      this.root.scale.fromArray(pose.scale)
      this.base.position.set(0, -16, 0) // half block down so transform is center block
      this.base.rotation.set(0, 0, 0)
      this.base.scale.set(1, 1, 1)
      this.gizmo.select(this.root, true)
    }
  }

  buildChildren(parent, node) {
    const obj = new THREE.Object3D()
    obj.position.fromArray(node.position)
    if (node.rotation) {
      degreesToEuler(node.rotation, obj.rotation)
    }
    if (node.scale) {
      obj.scale.fromArray(node.scale)
    }
    parent.add(obj)
    obj._nodeId = node.id
    this.objByNodeId[node.id] = obj
    if (node.type === 'box') {
      const mesh = this.buildBox(node)
      obj.add(mesh)
    }
    for (const child of node.children) {
      this.buildChildren(obj, child)
    }
  }

  buildBox(node) {
    m1.identity()
    const faces = []
    buildNodeFaces(m1, node, faces /*, [0, 0, 0, MAX_LIGHT_LEVEL], 1, this.asset.dimming*/)
    const out = {
      position: [],
      index: [],
      tileId: [],
      uv: [],
      light: [],
      ao: [],
      dim: [],
      wind: [],
    }
    let verts = 0
    for (const face of faces) {
      out.position.push(...face.position)
      for (let i = 0; i < face.index.length; i++) {
        out.index.push(verts + face.index[i])
      }
      verts += 4
      out.uv.push(...face.uv)
      for (let i = 0; i < 4; i++) {
        out.tileId.push(face.tileId)
        out.light.push(0, 0, 0, MAX_LIGHT_LEVEL)
        out.ao.push(1) // no ao here
        out.dim.push(this.asset.dimming ? face.dimFactor : 255)
      }
      out.wind.push(...face.wind)
    }
    const geometry = new THREE.BufferGeometry()
    const position = new Float32Array(out.position.length)
    for (let i = 0; i < out.position.length; i++) {
      position[i] = out.position[i]
    }
    const index = new Uint32Array(out.index.length)
    for (let i = 0; i < out.index.length; i++) {
      index[i] = out.index[i]
    }
    const tileId = new Uint16Array(out.tileId.length)
    for (let i = 0; i < out.tileId.length; i++) {
      tileId[i] = out.tileId[i]
    }
    const uv = new Uint8Array(out.uv.length)
    for (let i = 0; i < out.uv.length; i++) {
      uv[i] = out.uv[i]
    }
    const light = new Uint8Array(out.light.length)
    for (let i = 0; i < out.light.length; i++) {
      light[i] = out.light[i]
    }
    const ao = new Uint8Array(out.ao.length)
    for (let i = 0; i < out.ao.length; i++) {
      ao[i] = out.ao[i]
    }
    const dim = new Uint8Array(out.dim.length)
    for (let i = 0; i < out.dim.length; i++) {
      dim[i] = out.dim[i]
    }
    const wind = new Uint8Array(out.wind.length)
    for (let i = 0; i < out.wind.length; i++) {
      wind[i] = out.wind[i]
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(position, 3))
    // geometry.setAttribute('normal', new THREE.BufferAttribute(normal, 3))
    geometry.setAttribute('tileId', new THREE.BufferAttribute(tileId, 1))
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
    geometry.setAttribute('light', new THREE.BufferAttribute(light, 4))
    geometry.setAttribute('ao', new THREE.BufferAttribute(ao, 1))
    geometry.setAttribute('dim', new THREE.BufferAttribute(dim, 1))
    geometry.setAttribute('wind', new THREE.BufferAttribute(wind, 1))
    geometry.setIndex(new THREE.BufferAttribute(index, 1))
    const material = this.material.base
    const mesh = new THREE.Mesh(geometry, material)
    mesh.layers.enable(EditorAssetLayers.BOX)
    return mesh
  }

  setSelected(nodeId) {
    this.nodeId = nodeId
    this.gizmo.select(this.objByNodeId[this.nodeId], true)
  }

  setGizmo(type) {
    this.gizmo.setType(type)
  }

  setSpace(space) {
    this.gizmo.setSpace(space)
  }

  setGrid(grid) {
    this.gridVisible = grid
    this.grid.mesh.visible = grid
  }

  setSnap(snap) {
    this.snap = snap
    this.gizmo.setSnap(snap)
  }

  setMode(mode) {
    this.mode = mode
    this.checkView()
  }

  setPose(pose) {
    this.pose = pose
    this.checkView()
  }

  setActive(value) {
    if (this.active === value) return
    this.active = value
    this.renderer.setAnimationLoop(this.active ? this.update : null)
  }

  setAsset(asset, texture, ui) {
    this.asset = cloneDeep(asset)
    this.texture = texture
    this.ui = ui
    this.build()
  }

  checkView() {
    if (this.mode === 'build') {
      this.camera = this.cameraPers
      this.iconBG.visible = false
      this.gizmo.select(this.objByNodeId[this.nodeId], true)
      this.grid.mesh.visible = this.gridVisible
    }
    if (this.mode === 'paint') {
      this.camera = this.cameraPers
      this.iconBG.visible = false
      this.gizmo.select(null, true)
      this.grid.mesh.visible = this.gridVisible
    }
    if (this.mode === 'pose') {
      this.camera = this.pose === 'icon' ? this.cameraOrth : this.cameraPers
      this.iconBG.visible = this.pose === 'icon'
      this.gizmo.select(this.root, true)
      this.grid.mesh.visible = false
    }
    this.gizmo.control.camera = this.camera
  }

  async captureIcon(filename) {
    const ogMode = this.mode
    const ogPose = this.pose
    const ogWidth = this.width
    const ogHeight = this.height
    this.mode = 'pose'
    this.pose = 'icon'
    this.checkView()
    this.iconBG.visible = false
    this.build()
    this.gizmo.select(null, true)
    this.renderer.setPixelRatio(1)
    this.resize(180, 180) // 180px icon atm
    const canvas = this.renderer.domElement
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
    this.mode = ogMode
    this.pose = ogPose
    this.checkView()
    this.build()
    this.gizmo.select(this.gizmo.obj, true)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.resize(ogWidth, ogHeight)
    const data = new Uint8Array(await blob.arrayBuffer())
    return {
      width: 180,
      height: 180,
      data,
    }
    // const file = new File([blob], filename, { type: 'image/png' })
    // downloadFile(file)
    // return file
  }
}
